import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageSquare, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { Loading, PageHeader } from "../components/Shared";
import { useAuth } from "../context/AuthContext";

const ROLE_LABELS = { super_admin: "Super Admin", admin: "Admin", ops_manager: "Ops Manager",
  ops_executive: "Ops Executive", care_manager: "Care Manager" };

export default function Profile() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [channels, setChannels] = useState(null);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(null);
  const [showSms, setShowSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/auth/me").then((r) => { setMe(r.data); setPhone(r.data.phone || ""); }).catch((e) => toast.error(formatErr(e)));
    api.get("/notify/channel-status").then((r) => setChannels(r.data)).catch(() => setChannels(null));
  };
  useEffect(() => { load(); }, []);
  if (!me) return <Loading />;

  const digits = (me.phone || "").replace(/\D/g, "");
  const eligible = digits.length >= 10;
  const wa = channels?.whatsapp || {};
  const sms = channels?.sms || {};

  const savePhone = async () => {
    setBusy(true);
    try {
      await api.post("/phone/update", { phone });
      toast.success("Mobile number saved");
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const sendOtp = async () => {
    setBusy(true);
    try {
      const r = await api.post("/phone/send-otp", { phone });
      setOtpSent(r.data);
      toast.success("Verification code sent by SMS");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true);
    try {
      await api.post("/phone/verify", { code: otp });
      toast.success("Mobile verified via SMS (fallback method)");
      setOtpSent(null); setOtp(""); await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="staff-profile-page">
      <PageHeader title="My Alert Settings" sub={`${me.name} · ${ROLE_LABELS[me.role] || me.role}. Operational WhatsApp alerts come from ParentOne's official WhatsApp Business number to your registered work mobile.`} />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="p1-card p-6" data-testid="staff-mobile-card">
          <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><Phone className="h-5 w-5 text-emerald-800" /> Mobile number</p>
          <div className="mt-3 flex items-center gap-2">
            <input className="p1-input flex-1" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98XXXXXXXX" data-testid="staff-mobile-input" />
            <button onClick={savePhone} disabled={busy || phone.trim() === (me.phone || "")} className="p1-btn-primary !px-4 !py-2 text-xs disabled:opacity-40" data-testid="staff-phone-save">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
          {!eligible && (
            <p className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-xs font-semibold text-red-700" data-testid="staff-no-phone-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> No usable mobile on record — add your work mobile to receive WhatsApp operational alerts.
            </p>
          )}
          {eligible && (
            <p className="mt-3 rounded-xl bg-emerald-50 p-3 text-xs font-semibold text-emerald-900" data-testid="staff-phone-ok">
              Alerts will be sent to {me.phone} once WhatsApp delivery is live.
            </p>
          )}

          <div className="mt-4 border-t border-stone-100 pt-4">
            <button onClick={() => setShowSms(!showSms)} className="text-xs font-semibold text-stone-500 underline" data-testid="staff-sms-optional-toggle">
              Optional: verify mobile via SMS code (fallback method)
            </button>
            {showSms && (
              <div className="mt-3" data-testid="staff-sms-optional">
                <p className="mb-2 text-xs text-stone-400">SMS is a fallback channel and is NOT required for WhatsApp alerts. SMS sending activates after India DLT registration.</p>
                {!otpSent ? (
                  <button onClick={sendOtp} disabled={busy || digits.length < 10} className="p1-btn-outline !px-4 !py-2 text-xs disabled:opacity-40" data-testid="staff-send-otp">
                    {busy ? "Sending…" : me.phone_verified ? "Re-verify mobile (SMS code)" : "Verify mobile (SMS code)"}
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input className="p1-input flex-1" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" data-testid="staff-otp-input" />
                      <button onClick={verifyOtp} disabled={busy} className="p1-btn-primary !px-4 !py-2 text-xs" data-testid="staff-verify-otp">{busy ? "…" : "Verify"}</button>
                    </div>
                    {otpSent.test_mode && otpSent.test_code && (
                      <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800" data-testid="staff-otp-test-code">TEST mode — SMS is simulated. Code: <strong>{otpSent.test_code}</strong></p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p1-card p-6" data-testid="staff-channels-card">
          <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><MessageSquare className="h-5 w-5 text-emerald-800" /> Alert channels</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-stone-500">Mobile number</dt>
              <dd className="text-right font-semibold text-stone-800" data-testid="staff-mobile-status">{me.phone || "Not added"}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-stone-500">WhatsApp</dt>
              <dd className="text-right font-semibold" data-testid="staff-wa-status">
                {!wa.configured ? (
                  <span className="flex items-center justify-end gap-1 text-amber-700"><AlertTriangle className="h-4 w-4" /> WhatsApp integration not yet live</span>
                ) : wa.live ? (
                  me.wa_verified
                    ? <span className="flex items-center justify-end gap-1 text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Verified ✓</span>
                    : <span className="text-stone-600">Not connected</span>
                ) : (
                  <span className="text-amber-700">Configured — TEST mode (delivery simulated)</span>
                )}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-stone-500">SMS fallback</dt>
              <dd className="text-right text-xs font-semibold" data-testid="staff-sms-status">
                {sms.live
                  ? <span className="text-emerald-800">Available{me.phone_verified ? " · mobile verified" : ""}</span>
                  : <span className="text-stone-500">Not configured — activates after India DLT registration</span>}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-stone-500">Email</dt>
              <dd className="text-right text-xs font-semibold text-stone-600" data-testid="staff-email-status">
                {me.email_verified
                  ? <span className="flex items-center justify-end gap-1 text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Verified ✓ <span className="text-stone-500">({me.email})</span></span>
                  : me.email}
              </dd>
            </div>
          </dl>
          <p className="mt-4 rounded-xl bg-stone-50 p-3 text-xs text-stone-500">
            Staff alerts always come from ParentOne&apos;s official WhatsApp Business number — never your personal account. Your mobile is used only for operational notifications and is never shared with customers. SMS verification is optional and not required for WhatsApp alerts.
          </p>
        </div>
      </div>
    </div>
  );
}
